import React, { useRef } from 'react';
/**
 * DjayAvatar — render primitive (orchestrator).
 *
 * Split from the former ~1619-line `DjayAvatar.tsx` god-file into a scalable
 * `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1). This
 * orchestrator owns ONLY: size→detail-flag derivation, the unique gradient
 * `uid`, the outer <svg> shell + body gradient <defs>, and the assembly of the
 * body layers + the always-rendered floating accessory. All per-feature SVG
 * lives in `./parts/*`.
 *
 * This stays the canonical render primitive: product code must use the intent
 * wrappers `<DjayIdentity>` / `<DjaySpecimen>` / `<DjayNarrator>` instead of
 * rendering `<DjayAvatar>` directly (lint-guarded, see .eslintrc.json §6/§9.6).
 *
 * Import note (audit ronde 10): only `useRef` is needed from React — JSX is
 * transformed by the bundler. Adding unused React imports has regressed twice.
 */
import { SIZES, DJAY_VIEWBOX, DETAIL_THRESHOLD, ARM_STUB_MIN, MOUTH_MIN } from './types';
import type { DjayAvatarProps } from './types';
import { VARIANT_COLORS } from './palette';
import { ensureAnimationStyles } from './animationStyles';
import { BodyLayers } from './parts/BodyLayers';
import { Accessory } from './parts/Accessory';
import { Treatment } from './parts/Treatment';
export function DjayAvatar({
  size = 'md',
  emotion = 'happy',
  tier = 'rookie',
  variant = 'default',
  accessory = 'none',
  treatment = 'none',
  className = '',
  animate = true,
  hideBody = false
}: DjayAvatarProps) {
  const pixelSize = SIZES[size];
  const showDetails = pixelSize >= DETAIL_THRESHOLD;
  const showArmStubs = pixelSize >= ARM_STUB_MIN && pixelSize < DETAIL_THRESHOLD;
  const showFullArms = pixelSize >= DETAIL_THRESHOLD;
  const showTail = pixelSize >= DETAIL_THRESHOLD;
  const showMouth = pixelSize >= MOUTH_MIN;
  // If hideBody is true, force showAccessory regardless of size so we can
  // render just the accessory.
  const showAccessory = (pixelSize >= DETAIL_THRESHOLD || hideBody) && accessory !== 'none';
  const uidRef = useRef(`djay-${size}-${variant}-${Math.random().toString(36).slice(2, 6)}`);
  const uid = uidRef.current;
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const hasTreatment = treatment !== 'none' && !hideBody;
  // Glowing eyes accompany the lit finishes.
  const eyeGlow = treatment === 'glow' || treatment === 'gummy' || treatment === 'aura';
  // Inject animation styles once (singleton)
  if (animate) ensureAnimationStyles();
  return <div className={`relative inline-flex items-center justify-center ${className}`} data-id="element-3085" data-mp="15s4ev0o629ub">
      <svg width={pixelSize} height={pixelSize} viewBox={DJAY_VIEWBOX} fill="none" xmlns="http://www.w3.org/2000/svg" className={`djay-avatar ${animate ? 'djay-animated' : ''}`} style={{
      overflow: 'visible'
    }} role="img" aria-label={`Djay mascotte (${emotion})`} data-id="element-3086" data-mp="1jrdw83o3z9zv">
        <defs data-id="element-3087" data-mp="3hj8ftvucf4sk">
          {variant === 'rainbow' ? <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3088" data-mp="37e8i1lr7qzaq">
              <stop offset="0%" stopColor="#F87171" data-id="element-3089" data-mp="2xmvperxlc53n" />
              <stop offset="20%" stopColor="#FB923C" data-id="element-3090" data-mp="32lp2ij8ytu3a" />
              <stop offset="40%" stopColor="#FBBF24" data-id="element-3091" data-mp="32k494wye0h70" />
              <stop offset="60%" stopColor="#34D399" data-id="element-3092" data-mp="2nookj7gktdi0" />
              <stop offset="80%" stopColor="#60A5FA" data-id="element-3093" data-mp="2nraufdqxw53e" />
              <stop offset="100%" stopColor="#A78BFA" data-id="element-3094" data-mp="2sp41e902h34q" />
            </linearGradient> : <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3095" data-mp="2epl3e90yz865">
              <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3096" data-mp="cxg2w95en2jt" />
              <stop offset="45%" stopColor={colors.bodyGradient[1]} data-id="element-3097" data-mp="hw9jiafzn9o7" />
              <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3098" data-mp="2dz4k60a8berh" />
            </linearGradient>}
          {/* Soft top-left sheen — gives the body a rounded, lit-from-above
           read. Palette-agnostic (white→transparent), so it layers
           correctly over every variant without a per-variant entry. */}
          <radialGradient id={`${uid}-body-sheen`} cx="38%" cy="32%" r="60%" gradientUnits="objectBoundingBox" data-id="element-3099" data-mp="2004r8gfoodpi">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" data-id="element-3100" data-mp="1q91shla88ate" />
            <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.12" data-id="element-3101" data-mp="1q7qmrfx80szn" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" data-id="element-3102" data-mp="1lb06oslf1xe8" />
          </radialGradient>
          {/* Bottom inner shade — deepens the lower belly so the form has
           volume rather than reading flat. Uses the darkest gradient stop. */}
          <radialGradient id={`${uid}-body-shade`} cx="55%" cy="92%" r="62%" gradientUnits="objectBoundingBox" data-id="element-3103" data-mp="17axrje4hti2o">
            <stop offset="0%" stopColor={colors.bodyGradient[2]} stopOpacity="0.55" data-id="element-3104" data-mp="3nx469kuyj8qb" />
            <stop offset="60%" stopColor={colors.bodyGradient[2]} stopOpacity="0.14" data-id="element-3105" data-mp="3rzsk66yj35u5" />
            <stop offset="100%" stopColor={colors.bodyGradient[2]} stopOpacity="0" data-id="element-3106" data-mp="qbv6ywml98o" />
          </radialGradient>
          {hasTreatment && <>
              <radialGradient id={`${uid}-treatment-radial`} cx="50%" cy="55%" r="55%" data-id="element-3107" data-mp="39882w13uijw1">
                <stop offset="0%" stopColor={colors.treatmentAccent} stopOpacity="0.65" data-id="element-3108" data-mp="3e09ah9q2jmfq" />
                <stop offset="55%" stopColor={colors.treatmentAccent} stopOpacity="0.22" data-id="element-3109" data-mp="2lhvm00qmhctb" />
                <stop offset="100%" stopColor={colors.treatmentAccent} stopOpacity="0" data-id="element-3110" data-mp="3or82qaa4fvr7" />
              </radialGradient>
              <filter id={`${uid}-treatment-soft`} x="-30%" y="-30%" width="160%" height="160%" data-id="element-3111" data-mp="1il5k6os7y3w">
                <feGaussianBlur stdDeviation="4" data-id="element-3112" data-mp="35uysjy2cbpch" />
              </filter>
            </>}
        </defs>
        {hasTreatment && <g className="djay-treatment-under" data-id="element-3113" data-mp="3ev4zt27g3t37">
            <Treatment treatment={treatment} layer="under" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3114" data-mp="3ewy80lxpildb" />
          </g>}
        {!hideBody && <BodyLayers uid={uid} colors={colors} emotion={emotion} tier={tier} variant={variant} showTail={showTail} showDetails={showDetails} showMouth={showMouth} showFullArms={showFullArms} showArmStubs={showArmStubs} eyeGlow={eyeGlow} data-id="element-3115" data-mp="p6bg2aquovpz" />}
        {hasTreatment && <g className="djay-treatment-over" data-id="element-3116" data-mp="sn7acoypzk5l">
            <Treatment treatment={treatment} layer="over" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3117" data-mp="sq3749yu02sg" />
          </g>}
        <g className="djay-accessory-float" data-id="element-3118" data-mp="1leh9t30v7nac">
          <Accessory accessory={accessory} uid={uid} showAccessory={showAccessory} data-id="element-3119" data-mp="16mqivivg0xif" />
        </g>
      </svg>
    </div>;
}